import {
  IFileRequisites,
  IGenerateSignedUploadUrlInput,
  SourceEntityType,
  UploadingType,
} from '~/client/graph'
import {
  GetSignedUnverifiedUploadUrlDocument,
  VerifyUploadedFileDocument,
} from '~/client/graph/operations/generated/FileUploader.generated'
import Command from '~/client/src/shared/commands/Command'

import Localization from '../../localization/LocalizationManager'
import GraphExecutorStore from '../../stores/domain/GraphExecutor.store'
import { toast } from '../../utils/toast'
import { EMPTY_STRING } from '../../utils/usefulStrings'

export default class UploadFileCommand implements Command {
  public constructor(
    private readonly file: Blob,
    private readonly graphExecutorStore: GraphExecutorStore,
    private readonly fileName: string,
    private readonly uploadingType: UploadingType,
    private readonly isPublic: boolean,
    private readonly projectId: string,
    private readonly sourceEntityId: string,
    private readonly sourceEntityType: SourceEntityType,
    private readonly locationIds: string[],
    private readonly sourceEntityCode: string,
    private readonly sourceEntityTypeName: string,
    private readonly locationName: string,
    private readonly logUpload: boolean,
    private readonly isLogHidden: boolean,
  ) {}

  public async execute(): Promise<IFileRequisites[]> {
    const requisites = await this.getUploadingRequisites()
    const verifiedRequisites = []
    for (const requisite of requisites) {
      const verifiedRequisite = await this.uploadOnSignedUrl(requisite)
      if (verifiedRequisite) {
        verifiedRequisites.push(verifiedRequisite)
      }
    }
    return verifiedRequisites
  }

  private async getUploadingRequisites(): Promise<IFileRequisites[]> {
    const result = await this.graphExecutorStore.query(
      GetSignedUnverifiedUploadUrlDocument,
      {
        fileName: this.fileName,
        uploadingType: this.uploadingType,
      },
    )

    return result.data.getSignedUnverifiedUploadUrl
  }

  private async uploadOnSignedUrl(
    requisite: IFileRequisites,
  ): Promise<IFileRequisites> {
    if (!requisite.signedUrl || !requisite.contentType || !requisite.filePath) {
      throw new Error('Missing required upload requisites')
    }

    return await fetch(requisite.signedUrl, {
      method: 'PUT',
      headers: { 'Content-Type': requisite.contentType },
      body: this.file,
    }).then(async () => {
      const input: IGenerateSignedUploadUrlInput = {
        fileName: requisite.filePath,
        uploadingType: this.uploadingType,
        isPublic: this.isPublic,
        projectId: this.projectId,
        sourceEntityId: this.sourceEntityId,
        sourceEntityType: this.sourceEntityType || SourceEntityType.Form,
        locationIds: this.locationIds,
        sourceEntityCode: this.sourceEntityCode || EMPTY_STRING,
        sourceEntityTypeName: this.sourceEntityTypeName || EMPTY_STRING,
        locationName: this.locationName || EMPTY_STRING,
        logUpload: this.logUpload,
        isLogHidden: this.isLogHidden,
      }
      return await this.graphExecutorStore
        .query(VerifyUploadedFileDocument, {
          input,
        })
        .then(result => {
          return result.data.verifyUploadedFile
        })
        .catch(() => {
          this.handleUnsafeFileUpload(
            Localization.translator.failedToVerifyUploadedFile,
          )
          return null
        })
    })
  }

  private handleUnsafeFileUpload = (message: string) => {
    toast.error(message)
  }
}
